
import React, { useEffect, useState } from "react";
import axios from "axios";
import secureLocalStorage from "./storage";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Receipt } from "@mui/icons-material";

const PaymentHistory = () => {
  let token = secureLocalStorage.getItem("n_token");

  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const getStatusColor = (status) => {
    switch (status) {
      case "confirmed":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      case "expired":
        return "default";
      default:
        return "default";
    }
  };

  const formatStatusLabel = (status) => {
    if (!status) return "--";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const fetchClickHistory = async (status, pageNum) => {
    try {
      setLoading(true);
      const statusQuery = status && status !== "all" ? `&status=${status}` : "";

      const res = await axios.get(
        `${process.env.REACT_APP_API}website/my_click_history?page=${pageNum}&limit=10${statusQuery}`,
        { headers: { token } }
      );

      setHistoryData(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.log(err);
      setHistoryData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClickHistory(statusFilter, page);
  }, [statusFilter, page]);

  const handleFilterChange = (event, newFilter) => {
    if (newFilter !== null) {
      setStatusFilter(newFilter);
      setPage(1);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Receipt sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography variant="h4" component="h3" sx={{ fontWeight: 600 }}>
            Payments History
          </Typography>
        </Stack>

        {/* Status Filter Tabs */}
        <ToggleButtonGroup
          value={statusFilter}
          exclusive
          onChange={handleFilterChange}
          size="small"
          sx={{ flexWrap: "wrap" }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="pending">Pending</ToggleButton>
          <ToggleButton value="confirmed">Confirmed</ToggleButton>
          <ToggleButton value="rejected">Rejected</ToggleButton>
          <ToggleButton value="expired">Expired</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!loading && (
        <Paper
          elevation={2}
          sx={{
            width: "100%",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="cashback history table">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Store</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, textAlign: "center" }}>
                    Click Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, textAlign: "center" }}>
                    Order Value
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2, textAlign: "center" }}>
                    Cashback
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 2 }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData.map((item) => (
                  <TableRow
                    key={item.click_id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                      "&:hover": { backgroundColor: "action.hover" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    {/* Store */}
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        {/* <Avatar
                          // src={item.store_logo ? `/uploads/${item.store_logo}` : undefined}
                          src={item.store_logo ? `${process.env.REACT_APP_API}uploads/${item.store_logo}` : undefined}
                          alt={item.store_name}
                          sx={{ width: 32, height: 32 }}
                        >
                          {item.store_name?.charAt(0)}
                        </Avatar> */}
                        <Avatar
                          src={item.store_logo ? `${process.env.REACT_APP_API}uploads/${item.store_logo}` : undefined}
                          alt={item.store_name}
                          sx={{
                            width: 50,
                            height: 45,
                            bgcolor: "#fff",
                            border: "1px solid #eee",
                            "& img": {
                              objectFit: "contain",   // 🟢 ye zaroori hai - image ko stretch hone se rokega
                              padding: "0px",
                            },
                          }}
                        >
                          {item.store_name?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {item.store_name}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Click Date */}
                    <TableCell sx={{ py: 2, textAlign: "center" }}>
                      <Typography variant="body2">
                        {new Date(item.click_date).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </Typography>
                    </TableCell>

                    {/* Order Value */}
                    <TableCell sx={{ py: 2, textAlign: "center" }}>
                      <Typography variant="body2">
                        {item.order_value > 0 ? `₹${item.order_value}` : "--"}
                      </Typography>
                    </TableCell>

                    {/* Cashback Amount */}
                    <TableCell sx={{ py: 2, textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color:
                            item.status === "confirmed"
                              ? "success.main"
                              : "text.secondary",
                        }}
                      >
                        {item.cashback_amount > 0
                          ? `₹${item.cashback_amount}`
                          : "--"}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={formatStatusLabel(item.status)}
                        color={getStatusColor(item.status)}
                        size="small"
                        variant={item.status === "pending" ? "outlined" : "filled"}
                      />
                    </TableCell>

                    {/* Details (expected confirmation / rejection reason) */}
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        {item.status === "pending" && item.expected_confirmation_text}
                        {item.status === "rejected" &&
                          `Reason: ${item.rejection_reason}`}
                        {item.status === "expired" &&
                          "The expected confirmation time has passed. Your cashback will be added once the store confirms your order."}
                        {item.status === "confirmed" && "--"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {/* {totalPages > 1 && (
              <div className="ph-pagination">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  &lt;
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    (p) =>
                      p === 1 ||
                      p === totalPages ||
                      Math.abs(p - page) <= 1
                  )
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && (
                        <span className="ph-page-dots">...</span>
                      )}

                      <button
                        className={page === p ? "active" : ""}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}


                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )} */}

            {totalPages > 1 && (
              <div className="od-pagination">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && <span className="od-page-dots">...</span>}
                      <button
                        className={page === p ? "active" : ""}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}

                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
          </TableContainer>

          {/* Empty State */}
          {historyData.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
              <Receipt sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
              <Typography variant="h6" gutterBottom>
                No transactions yet
              </Typography>
              <Typography variant="body2">
                Your cashback history will appear here once you shop through a
                store.
              </Typography>
            </Box>
          )}
        </Paper>
      )}
      <style jsx="true">{`
        .od-breadcrumb {
          padding: 12px 0;
          font-size: 0.9rem;
          color: #555;
        }
        .od-breadcrumb span {
          cursor: pointer;
          color: #c01e12;
        }
        .od-breadcrumb b {
          color: #333;
        }
        .od-section {
          padding: 2rem 0 4rem;
        }
        .od-filters {
          display: flex;
          gap: 10px;
          margin-bottom: 1.2rem;
          flex-wrap: wrap;
        }
        .od-filter-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border-radius: 10px;
          border: 1px solid #ddd;
          background: #fff;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.85rem;
          color: #444;
          transition: all 0.2s ease;
        }
      .od-filter-btn.active {
  background: var(--primary-gradient);
  color: #fff;
  border-color: #c01e12;
}
        .od-filter-count {
          background: rgba(0,0,0,0.08);
          color: inherit;
          font-size: 0.7rem;
          padding: 1px 7px;
          border-radius: 10px;
        }
        .od-filter-btn.active .od-filter-count {
          background: rgba(255,255,255,0.25);
        }
        .od-box {
          border: 1px solid #eee;
          border-radius: 12px;
          padding: 1.2rem;
          background: #fff;
        }
        .od-loading, .od-empty-text {
          text-align: center;
          color: #777;
          padding: 2.5rem 0;
        }

        .od-empty-state {
          text-align: center;
          max-width: 480px;
          margin: 0 auto;
        }
        .od-empty-state h2 {
          font-weight: 700;
          margin: 1.5rem 0 0.5rem;
          color: #222;
        }
        .od-empty-state p {
          color: #777;
          margin-bottom: 1.8rem;
        }
        .od-empty-illustration {
          width: 100%;
          max-width: 476px;
          height: auto;
          object-fit: contain;
          margin-bottom: 0.5rem;
        }
        .od-cta-btn {
          background: #ff5a1f;
          color: #fff;
          border: none;
          padding: 0.9rem 3rem;
          border-radius: 6px;
          font-weight: 700;
          letter-spacing: 0.5px;
          cursor: pointer;
          transition: opacity 0.2s ease;
        }
        .od-cta-btn:hover {
          opacity: 0.9;
        }

        .od-orders-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .od-order-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          border: 1px solid #eee;
          border-radius: 10px;
          padding: 12px 16px;
          background: #fff;
          transition: box-shadow 0.2s ease;
        }
        .od-order-card:hover {
          box-shadow: 0 3px 12px rgba(0,0,0,0.06);
        }
        .od-order-logo-wrap {
          width: 100px;
          height: 72px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #eee;
          border-radius: 10px;
          padding: 6px;
          background: #fafafa;
        }
        .od-order-logo-wrap img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        .od-order-info {
          flex: 1;
          min-width: 0;
        }
        .od-order-retailer {
          font-weight: 700;
          margin: 0 0 4px;
          color: #1a1a1a;
          font-size: 1rem;
        }
        .od-order-date {
          font-size: 0.82rem;
          color: #777;
          margin: 0 0 2px;
        }
        .od-order-ref {
          font-size: 0.75rem;
          color: #aaa;
          margin: 0 0 4px;
        }
        .od-order-amount {
          font-weight: 600;
          margin: 4px 0 0;
          color: #333;
          font-size: 0.9rem;
        }
        .od-order-reason {
          font-size: 0.8rem;
          color: #888;
          margin: 4px 0 0;
        }
        .od-order-status {
          text-align: right;
          flex-shrink: 0;
        }
        .od-status-badge {
          display: inline-block;
          padding: 5px 14px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: capitalize;
          white-space: nowrap;
        }
        .od-status-badge.pending {
          background: #fff3cd;
          color: #997404;
        }
        .od-status-badge.confirmed {
          background: #d1e7dd;
          color: #0f5132;
        }
        .od-status-badge.rejected {
          background: #f8d7da;
          color: #842029;
        }
        .od-status-badge.expired {
          background: #eee;
          color: #777;
        }
        .od-order-cashback {
          font-size: 0.95rem;
          font-weight: 700;
          color: #22c55e;
          margin: 8px 0 0;
        }
.od-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 1.5rem;
  padding-bottom: 1.5rem;
  flex-wrap: wrap;
}
        .od-pagination button {
          min-width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 1px solid #ddd;
          background: #fff;
          color: #444;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.85rem;
        }
        .od-pagination button.active {
          background: #2f5bff;
          color: #fff;
          border-color: #2f5bff;
        }
        .od-pagination button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .od-page-dots {
          color: #999;
          padding: 0 2px;
        }

        @media (max-width: 576px) {
          .od-order-card {
            flex-wrap: wrap;
          }
          .od-order-status {
            text-align: left;
            width: 100%;
          }
        }
      `}</style>
    </Box>
  );
};

export default PaymentHistory;

